// Shared failure-reporting seam for theme surfaces.
//
// `assets/et-sentry.js` installs `window.etObservability`, which answers "did
// something throw". It does not answer the question the business cares about:
// "did the shopper's request quietly fail while the page kept going". The theme
// has ~325 catch blocks and ~130 fetch call sites; nearly all of them degrade
// gracefully and log to `console.error`, which never leaves the browser.
//
// This is the one seam those paths report through, so instrumenting a call site
// is a rename rather than a bespoke reporter per file.
//
// TWO HARD RULES, because this instruments shopper-facing legacy code:
//
//  1. NOTHING HERE MAY CHANGE BEHAVIOR. `report.fetch` is a pure observer: same
//     Response returned, same error rethrown, thrown the same way (a synchronous
//     throw stays synchronous). An observability layer that alters control flow
//     is a bug factory.
//  2. NOTHING HERE MAY THROW. Callers invoke this from `catch` blocks, so an
//     escaping throw would skip their recovery path -- the exact failure the
//     reporting exists to surface.
(function () {
  // Per-issue and per-pageload event caps. Instrumented paths retry and poll, so
  // one shopper hitting a down backend can emit the same failure dozens of
  // times. Uncapped, one backend incident becomes a quota incident -- and
  // Sentry's quota drop is indiscriminate, so it would blind us to unrelated
  // errors at the same time.
  var MAX_EVENTS_PER_ISSUE = 3;
  var MAX_EVENTS_PER_PAGELOAD = 40;

  // How long after the page comes back a rejection still reads as the iOS
  // freeze/flush rather than a fresh fault. iOS suspends JS while the app is
  // backgrounded and drains the queued microtasks on resume, so a request that
  // died during the freeze surfaces in the first moments after `pageshow`. A
  // request that outlives that window and THEN fails, failed on its own merits.
  var RESUME_FLUSH_MS = 2000;

  var state = {
    issueCounts: {},
    totalEvents: 0,
    reporters: {},
    // Bumped every time the page is backgrounded, suspended, or navigated away
    // from. A request whose epoch moved while it was in flight was torn down by
    // the page, not by a fault. A counter rather than a latched boolean:
    // `pagehide` also fires on the way INTO the back/forward cache, and a latch
    // would silence a restored page for the rest of its life.
    hiddenEpoch: 0,
    // True between `pagehide` and the next `pageshow`: the document is being
    // torn down. Distinct from "hidden" ON PURPOSE -- desktop browsers run
    // fetches normally in a background tab, so a rejection there is a genuine
    // fault and must still be reported. A hidden tab is never on its own enough
    // to call something a teardown.
    unloading: false,
    // When the page last came to the front. Bounds how long after a resume a
    // rejection is still attributed to the freeze; without it, one tab switch
    // would suppress that request's failure for the rest of its life.
    lastShownAt: 0,
    // Rejections `observedFetch` identified as page teardown. Call sites pair
    // `fetch` with a `failure` in their catch block, so the SAME value arrives
    // at `report` twice by two different doors. Marking it here and dropping it
    // in `report` is what makes the guard actually remove the noise instead of
    // relabelling it under the catch block's slug (SHOPIFY-3K0 -> 3K1).
    //
    // A WeakSet rather than a flag on the error: the thrown value belongs to the
    // caller, and this file does not mutate what it observes (HARD RULE 1).
    lifecycleAborts: typeof WeakSet === 'function' ? new WeakSet() : null,
    // Values this module HAS reported -- the mirror image of `lifecycleAborts`,
    // which holds what it deliberately did not. A call site that rethrows to
    // keep its own control flow asks `wasReported` before filing from its catch,
    // so one failure stays one issue (SHOPIFY-3K0 and SHOPIFY-3K1 were one 503
    // under two fingerprints). A WeakSet for the same reason as above: the
    // Response and the thrown value belong to the caller (HARD RULE 1).
    reportedValues: typeof WeakSet === 'function' ? new WeakSet() : null
  };

  function freezeValue(value) {
    if (typeof Object.freeze === 'function') return Object.freeze(value);
    return value;
  }

  // Read through to `window` on every call rather than capturing at load time.
  // et-sentry.js swaps its no-op facade for the real one inside `sentryOnLoad`,
  // which fires when the remote loader lands -- after this asset has evaluated.
  function getObservability() {
    var observability = window.etObservability;

    if (!observability || typeof observability !== 'object') return null;
    if (typeof observability.captureException !== 'function') return null;
    return observability;
  }

  // URL templating lives in et-sentry.js because that file also has to scrub
  // breadcrumbs and request context, which no consumer can reach. If it is
  // somehow unavailable, drop the URL rather than fall back to the raw value --
  // the storefront puts zip codes in the path, so a raw URL is a PII leak.
  function templateUrl(rawUrl) {
    var sentry = window.ET && window.ET.Sentry;

    if (!sentry || typeof sentry.templateUrl !== 'function') return '';
    try {
      return sentry.templateUrl(rawUrl);
    } catch (error) {
      return '';
    }
  }

  function withinBudget(issue) {
    if (state.totalEvents >= MAX_EVENTS_PER_PAGELOAD) return false;

    var seen = state.issueCounts[issue] || 0;
    if (seen >= MAX_EVENTS_PER_ISSUE) return false;

    state.issueCounts[issue] = seen + 1;
    state.totalEvents += 1;
    return true;
  }

  function normalizeIssue(issue) {
    return (typeof issue === 'string' && issue.trim()) || 'unspecified-failure';
  }

  // Resolves the URL of anything `fetch` accepts: a string, a URL, or a Request.
  function resolveRequestUrl(resource) {
    if (typeof resource === 'string') return resource;
    if (!resource) return '';

    try {
      if (typeof resource.url === 'string' && resource.url) return resource.url;
      return String(resource);
    } catch (error) {
      return '';
    }
  }

  function resolveMethod(resource, init) {
    var method = (init && init.method) || (resource && resource.method);
    return (typeof method === 'string' && method ? method : 'GET').toUpperCase();
  }

  function isExpectedStatus(status, expected) {
    return Array.isArray(expected) && expected.indexOf(status) !== -1;
  }

  /**
   * Records that this module reported a value, and hands the value back.
   *
   * Applied to whatever the caller ends up holding: the rethrown error on a
   * rejection, the Response on a non-ok status, or an error the caller builds
   * from that Response for its own control flow.
   *
   * Set ONLY when an event really went out -- never on the assumption that a
   * report site ran. `report` files nothing once the per-issue budget is spent,
   * when `expect` covers the status, when the page is unloading, or when
   * observability is absent, and a caller deduping against an unearned mark
   * suppresses its own report too, which loses the failure altogether.
   *
   * Nothing is written to the value itself: a WeakSet keeps this file from
   * mutating what it observes (HARD RULE 1), matching `lifecycleAborts`.
   *
   * @param {*} value Value to record, returned unchanged.
   */
  function markReported(value) {
    if (!state.reportedValues || !isTrackable(value)) return value;
    try {
      state.reportedValues.add(value);
    } catch (trackingError) {
      // Losing the mark only costs a duplicate event, which is the old
      // behavior -- never worth throwing into a caller's catch (HARD RULE 2).
    }
    return value;
  }

  /**
   * True when `markReported` recorded this value.
   *
   * Read from inside a recovery `catch` before its `return false`, so HARD RULE
   * 2 applies: an unanswerable value reports "not reported" rather than escape
   * and skip the recovery this exists to observe.
   */
  function wasReported(value) {
    if (!state.reportedValues || !isTrackable(value)) return false;
    try {
      return state.reportedValues.has(value);
    } catch (trackingError) {
      return false;
    }
  }

  function currentPathname() {
    return (window.location && window.location.pathname) || '';
  }

  function pageIsHidden() {
    var doc = window.document;

    if (!doc) return false;
    return doc.visibilityState === 'hidden';
  }

  /**
   * Watches for the page going away, so `observedFetch` can tell a teardown
   * from a real network fault.
   *
   * iOS is why this exists. WebKit rejects a request with `TypeError: Load
   * failed` the moment the document unloads or the tab is suspended -- observed
   * both at 1ms (the shopper tapped a link) and at 275s (the shopper locked
   * their phone). Neither is visible to them: the work is retried, and the next
   * page load re-syncs. Reporting them spent quota and paged on normal mobile
   * browsing (SHOPIFY-3K0).
   *
   * `visibilitychange` catches the suspended tab; `pagehide` catches the
   * navigation, and is the only unload-ish event iOS fires reliably.
   */
  function watchPageLifecycle() {
    function markHidden() {
      state.hiddenEpoch += 1;
    }

    try {
      if (typeof window.addEventListener === 'function') {
        window.addEventListener('pagehide', function () {
          state.unloading = true;
          markHidden();
        });
        // Fires on a fresh load AND on a back/forward-cache restore, which is
        // what stops `unloading` latching for the life of a restored page.
        window.addEventListener('pageshow', function () {
          state.unloading = false;
          state.lastShownAt = Date.now();
        });
      }

      var doc = window.document;
      if (doc && typeof doc.addEventListener === 'function') {
        doc.addEventListener('visibilitychange', function () {
          if (pageIsHidden()) markHidden();
          else state.lastShownAt = Date.now();
        });
      }
    } catch (error) {
      // An environment that refuses listeners simply keeps reporting everything.
    }
  }

  /** Only objects can be tracked; a thrown string is reported as it always was. */
  function isTrackable(value) {
    return Boolean(value) && (typeof value === 'object' || typeof value === 'function');
  }

  function canTrack(error) {
    return Boolean(state.lifecycleAborts) && isTrackable(error);
  }

  /**
   * Reads `name` off a foreign thrown value without trusting its getter. Every
   * other foreign-value access in this file is defensive for the same reason:
   * a throw here would escape `onRejected` and reject the caller's promise with
   * the REPORTER's error instead of their own (HARD RULE 1).
   */
  function errorName(error) {
    try {
      return (error && error.name) || '';
    } catch (accessError) {
      return '';
    }
  }

  /**
   * True when a rejected request was torn down by the page rather than failing
   * on its own merits: an explicit abort, an unload in progress, or a request
   * that both crossed a visibility change AND failed in the shape of an iOS
   * freeze -- while still hidden, or as the queue drained on resume.
   *
   * Deliberately narrow in three ways.
   *
   * It gates only the REJECTION branch, so an unexpected HTTP status is still
   * reported and a real outage cannot hide behind a backgrounded tab.
   *
   * A hidden tab is never on its own enough. Desktop and Android keep
   * background fetches running, so a rejection there is a real fault -- exactly
   * the cross-origin admin-app signal SHOPIFY-3K2 is made of. Suppression needs
   * either an unload, or a visibility change the request did not survive; a
   * request that outlives the resume and fails later is reported.
   *
   * And it leaves untrackable values alone, so both doors of the seam agree on
   * every value.
   */
  function isLifecycleAbort(error, startedHiddenEpoch) {
    // `markLifecycleAbort` cannot record a value a WeakSet will not hold, so
    // suppressing one here would drop it at THIS door while the caller's catch
    // still reports it through `failure` -- the sibling-slug relabelling this
    // guard exists to prevent.
    if (!canTrack(error)) return false;
    // ASSUMPTION: no instrumented call site aborts on a timeout. The two
    // AbortController users in the theme (product-info.js, predictive-search.js)
    // abort a superseded request, which is not a fault. If an instrumented site
    // ever adds `setTimeout(() => controller.abort(), n)`, a dead backend would
    // reject with AbortError and be silently dropped here -- narrow this first.
    if (errorName(error) === 'AbortError') return true;
    if (state.unloading) return true;
    if (state.hiddenEpoch === startedHiddenEpoch) return false;
    return pageIsHidden() || Date.now() - state.lastShownAt < RESUME_FLUSH_MS;
  }

  function markLifecycleAbort(error) {
    if (!canTrack(error)) return;
    try {
      state.lifecycleAborts.add(error);
    } catch (trackingError) {
      // Losing the mark only means the failure is reported, which is the old
      // behavior -- never worth throwing into a caller's catch (HARD RULE 2).
    }
  }

  function wasLifecycleAbort(error) {
    if (!canTrack(error)) return false;
    try {
      return state.lifecycleAborts.has(error);
    } catch (trackingError) {
      return false;
    }
  }

  /**
   * An HTTP response the caller treated as a failure.
   *
   * A non-2xx `fetch` resolves successfully, so there is no thrown value for
   * Sentry to group on. A real Error with a stable name/message makes the issue
   * read as `EtHttpError: GET .../availability/:id -> 503` rather than
   * collapsing into an untyped message capture.
   */
  function buildHttpError(method, templatedUrl, status) {
    var error = new Error(method + ' ' + templatedUrl + ' -> ' + (status || 'network-error'));

    error.name = 'EtHttpError';
    return error;
  }

  /**
   * Reports a swallowed failure to Sentry.
   *
   * @param {string} surface Bound by `forSurface`.
   * @param {string} issue Stable kebab-case slug. Becomes the `issue` tag and
   *   part of the fingerprint, so one report site keeps one issue in Sentry.
   * @param {*} error Thrown value. Pass it RAW -- et-sentry.js types non-Errors
   *   and records what actually threw; pre-wrapping destroys the cause
   *   (SHOPIFY-3HD).
   * @param {Object} [options]
   * @param {Object} [options.extra] Extra fields for the event.
   * @param {string} [options.level] Sentry level, default `error`.
   * @returns {string|null} Sentry event id, or null when nothing was sent.
   */
  function report(surface, issue, error, options) {
    var observability = getObservability();
    var normalizedIssue = normalizeIssue(issue);
    var opts = options || {};

    if (!observability) return null;
    // Both checked before the budget so a teardown cannot spend a real
    // failure's slot.
    //
    // The unload check is what covers values `observedFetch` never saw: a
    // `Response` body read (`await res.json()`) that rejects mid-unload is
    // caught by the CALL SITE, not by the fetch observer, and every
    // instrumented site reports that through `failure` -- the
    // `cart-fulfillment-date-update-failed` slug is one such catch block.
    // Without this the noise just moves to that slug.
    if (state.unloading) return null;
    if (wasLifecycleAbort(error)) return null;
    if (!withinBudget(surface + ':' + normalizedIssue)) return null;

    try {
      return observability.captureException(error, {
        level: opts.level || 'error',
        // Titles the event only when the thrown value carries no message of its
        // own, matching the `[surface] issue` convention already in use.
        fallbackMessage: '[' + surface + '] ' + normalizedIssue,
        tags: { surface: surface, issue: normalizedIssue },
        // Pin grouping to the report site. These paths swallow heterogeneous
        // causes (a 503, a JSON parse error, an aborted fetch) that stack-trace
        // grouping would scatter across issues, none of which reads as "the zip
        // lookup is broken" (SHOPIFY-3H9).
        fingerprint: ['et-report', surface, normalizedIssue],
        extra: Object.assign({}, opts.extra, { pathname: currentPathname() })
      });
    } catch (reportingError) {
      // Reporting must never break the recovery path it was added to observe.
      return null;
    }
  }

  /**
   * Observes a `fetch` call and reports failures. Does not change its result.
   *
   * @param {string} surface Bound by `forSurface`.
   * @param {string} issue Stable slug for this request's failure mode.
   * @param {RequestInfo} resource
   * @param {Object} [init]
   * @param {Object} [options]
   * @param {number[]} [options.expect] Statuses that are a normal outcome here
   *   and must NOT be reported. The serviceability lookup answers "we do not
   *   deliver to this zip" with a 404 -- a business result, not a fault.
   * @param {boolean} [options.transientBackend] This endpoint is not ours (e.g.
   *   Shopify's own `/cart/*.js`), so a 5xx from it is a platform blip rather
   *   than our outage and reports at `warning` instead of `error`. Still
   *   reported -- a sustained one is exactly what blocks every add-to-cart --
   *   just not at paging severity.
   *
   *   Declared per call site, not derived from the URL, because the claim being
   *   made is about the CALLER ("this path recovers"), not the host. Set it ONLY
   *   where that holds: the caller retries, or a downstream gate re-verifies
   *   before the shopper can act on stale state. Deriving it from the host would
   *   silently apply it at sites that swallow the failure instead.
   *
   *   Scope: response statuses only. A network rejection is still `error` --
   *   that is the shopper's connection as often as the platform, and this
   *   option cannot tell them apart.
   * @param {Object} [options.extra] Extra fields for the event.
   * @returns {Promise<Response>}
   */
  function observedFetch(surface, issue, resource, init, options) {
    var opts = options || {};
    var startedAt = Date.now();
    var startedHiddenEpoch = state.hiddenEpoch;
    var method = resolveMethod(resource, init);
    var templatedUrl;

    // Resolved lazily: only the failure branches read it, and the success path
    // is the overwhelming majority of requests.
    function requestUrl() {
      if (templatedUrl === undefined) templatedUrl = templateUrl(resolveRequestUrl(resource));
      return templatedUrl;
    }

    function requestExtra(responseStatus) {
      return Object.assign({}, opts.extra, {
        requestUrl: requestUrl(),
        requestMethod: method,
        durationMs: Date.now() - startedAt,
        responseStatus: responseStatus
      });
    }

    // Called directly, not wrapped in Promise.resolve(): a synchronous throw
    // must stay synchronous to honour HARD RULE 1.
    return window.fetch(resource, init).then(
      function (response) {
        var status = response && typeof response.status === 'number' ? response.status : 0;

        if (response && response.ok) return response;
        if (isExpectedStatus(status, opts.expect)) return response;

        var eventId = report(surface, issue, buildHttpError(method, requestUrl(), status), {
          // A 5xx is our backend failing. A 4xx the caller did not declare is
          // contract drift (bad id, expired token, changed route) -- real, but
          // not an outage, so it must not page anyone. `transientBackend`
          // withdraws the 5xx half: on an endpoint we do not own, that is the
          // platform blipping, not our outage. (SHOPIFY-3K0)
          level: status === 0 || (status >= 500 && !opts.transientBackend) ? 'error' : 'warning',
          extra: requestExtra(status)
        });

        // Marked only when an event actually went out. `report` files nothing
        // once the per-issue budget is spent or when observability is absent, so
        // a caller that deduped on the ASSUMPTION this reported would silence
        // both issues and leave the failure invisible. The mark rides on the
        // Response because that is what the caller holds in its `!ok` branch.
        if (eventId) markReported(response);

        return response;
      },
      function (error) {
        // A rejected fetch is a network fault, an abort, or a CORS failure --
        // indistinguishable here, and all invisible to the shopper except as a
        // feature that silently did nothing.
        //
        // Unless the page itself tore it down, which is not a fault: it is a
        // shopper tapping a link or switching apps, and the work resumes on the
        // next page load. Rethrow without reporting -- the caller's recovery
        // path must still run (HARD RULE 1).
        if (isLifecycleAbort(error, startedHiddenEpoch)) {
          // Marked, not just skipped: the caller's catch block reports this same
          // value through `failure`, and that door has to drop it too.
          markLifecycleAbort(error);
          throw error;
        }

        var eventId = report(surface, issue, error, {
          level: 'error',
          extra: requestExtra('network-error')
        });

        // Rethrown identically (HARD RULE 1). Marked only when it was really
        // filed, for the same reason as the non-ok branch above.
        if (eventId) markReported(error);
        throw error;
      }
    );
  }

  /**
   * Binds a surface name so call sites only name the failure, not the file.
   *
   * Memoized per surface, so a consumer can call this inline at each site
   * without building a reporter every time.
   *
   * ONE FAILURE, ONE ISSUE. A path that catches what `fetch` already reported
   * must not file it again: `fetch` marks what it actually sent, so the catch
   * checks `wasReported` first (SHOPIFY-3K0 and SHOPIFY-3K1 were one 503 under
   * two fingerprints). Check the mark; never assume the report happened, since
   * `expect`, a spent budget, or a missing facade all return without filing.
   * The exception is a second slug that names a genuinely
   * DIFFERENT event -- `zip-verification-blocked-shopper` is the shopper being
   * turned away, not the lookup that failed, so it is reported unconditionally.
   * Same information => dedupe; distinct shopper-visible outcome => keep both.
   *
   * @param {string} surface e.g. 'delivery-modal'
   * @returns {{failure: Function, fetch: Function, markReported: Function,
   *   wasReported: Function}} Frozen. A consumer that stubs this when the asset
   *   fails to load must answer every member, or reporting code becomes a
   *   TypeError on the path it only meant to observe.
   */
  function forSurface(surface) {
    var boundSurface =
      typeof surface === 'string' && surface.trim() ? surface.trim() : 'unknown-surface';

    if (state.reporters[boundSurface]) return state.reporters[boundSurface];

    state.reporters[boundSurface] = freezeValue({
      failure: function (issue, error, options) {
        return report(boundSurface, issue, error, options);
      },
      fetch: function (issue, resource, init, options) {
        return observedFetch(boundSurface, issue, resource, init, options);
      },
      markReported: markReported,
      wasReported: wasReported
    });

    return state.reporters[boundSurface];
  }

  watchPageLifecycle();

  window.etReport = freezeValue({
    forSurface: forSurface,
    // For callers that report through their own channel but still must not
    // file a page teardown as a failure (recharge-utils, #342).
    isUnloading: function () {
      return state.unloading;
    }
  });
})();
